import { VariableType } from 'json-to-graphql-query';

import { Profile } from '@/interfaces/profile';
import { Tokens } from '@/interfaces/tokens';
import { LoginArgs, LoginVariables } from './queryVariables/login';
import { RegisterArgs, RegisterVariables } from './queryVariables/register';
import { Resource } from './resource';

/**
 * Resource autentykacji
 *
 * @export
 * @class AuthResource
 * @extends {Resource}
 */
export class AuthResource extends Resource {
  /**
   * Logownaie
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{ token: string; profile: Profile }>}
   * @memberof AuthResource
   */
  public login(email: string, password: string): Promise<{ token: string; profile: Profile }> {
    const query = this.prepareOptions({
      query: {
        __variables: { ...LoginVariables },
        accountLogin: {
          __args: { ...LoginArgs },
          token: true,
          profile: {
            id: true,
            firstName: true,
            lastName: true,
            email: true,
            mainImageId: true,
            lang: true
          }
        }
      }
    });
    return this.query({ query, variables: { login: email, password } }, 'accountLogin');
  }

  /**
   * Rejestracja
   *
   * @param {*} profile
   * @param {Tokens} tokens
   * @param {number[]} termsIds
   * @returns {Promise<string>}
   * @memberof AuthResource
   */
  public register(profile: any, tokens: Tokens, termsIds: number[]): Promise<string> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: RegisterVariables,
        accountRegister: { __args: RegisterArgs }
      }
    });
    return this.mutation({ mutation, variables: { profile, tokens, termsIds } }, 'accountRegister');
  }

  /**
   * Sprawdza uprawnienie użytkownika
   *
   * @param {string[]} permissions
   * @returns {Promise<boolean[]>}
   * @memberof AuthResource
   */
  public checkPermissions(permissions: string[]): Promise<boolean[]> {
    const query = this.prepareOptions({
      query: {
        __variables: { permissions: '[String!]!' },
        checkPermissions: { __args: { permissions: new VariableType('permissions') } }
      }
    });
    return this.query({ query, variables: { permissions } }, 'checkPermissions');
  }

  /**
   * Potwierdzenie konta
   *
   * @param {string} token
   * @param {string} password
   * @returns {Promise<number>}
   * @memberof AuthResource
   */
  public async confirm(token: string, password: string): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', token: 'String!' },
        accountConfirm: {
          __args: { password: new VariableType('password'), token: new VariableType('token') }
        }
      }
    });
    return this.mutation(
      {
        mutation,
        variables: { token, password }
      },
      'accountConfirm'
    );
  }

  /**
   * Potwierdzenie konta z akceptacją regulaminów
   *
   * @param {string} token
   * @param {string} password
   * @param {number[]} termsIds
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public async acceptTerms(token: string, password: string, termsIds: number[]): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', token: 'String!', termsIds: '[Int!]' },
        accountAcceptTerms: {
          __args: {
            password: new VariableType('password'),
            token: new VariableType('token'),
            termsIds: new VariableType('termsIds')
          }
        }
      }
    });
    return this.mutation(
      {
        mutation,
        variables: { token, password, termsIds }
      },
      'accountAcceptTerms'
    );
  }

  /**
   * Reset hasła
   *
   * @param {string} email
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public resetPassword(email: string): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { email: 'String!' },
        accountResetPassword: { __args: { email: new VariableType('email') } }
      }
    });
    return this.mutation({ mutation, variables: { email } }, 'accountResetPassword');
  }

  /**
   * Zamiana hasła użytkownika
   *
   * @param {string} token
   * @param {string} password
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public async setPassword(token: string, password: string): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', token: 'String!' },
        accountSetPassword: {
          __args: { password: new VariableType('password'), token: new VariableType('token') }
        }
      }
    });
    return this.mutation({ mutation, variables: { token, password } }, 'accountSetPassword');
  }

  /**
   * Zmiana hasła zalogowanego użytkownika
   *
   * @param {number} profileId
   * @param {{ password: string; currentPassword: string }} data
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public async setPasswordLogged(
    profileId: number,
    data: { password: string; currentPassword: string }
  ): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', currentPassword: 'String!', profileId: 'Int!' },
        accountSetPasswordLogged: {
          __args: {
            password: new VariableType('password'),
            currentPassword: new VariableType('currentPassword'),
            profileId: new VariableType('profileId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { profileId, ...data } }, 'accountSetPasswordLogged');
  }
}

export const authResource = new AuthResource();
